// Long-form category articles linked from the "Categories of Help" grid on Home.jsx.
// Add a new entry here to publish a fresh category article — the "Learn More →" link
// on Home.jsx will automatically appear next to that category's heading.

const IMG_HOUSING =
  "https://images.unsplash.com/photo-1516880711640-ef7db81be3e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxidWlsZGluZyUyMHdvb2RlbiUyMGhvdXNlJTIwcHJvamVjdHxlbnwwfHx8fDE3ODQ1NzE3NzB8MA&ixlib=rb-4.1.0&q=85";

const IMG_FOOD =
  "https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const IMG_EDUCATION =
  "https://images.unsplash.com/photo-1758599669199-a858720a9689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHx2b2x1bnRlZXJzJTIwaGVscGluZyUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODQ1NzE3NzB8MA&ixlib=rb-4.1.0&q=85";

const IMG_MEDICAL =
  "https://images.unsplash.com/photo-1665062173657-3912ae7991e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwc2VuaW9yJTIwY2l0aXplbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDU3MTc3MHww&ixlib=rb-4.1.0&q=85";

const IMG_SENIORS =
  "https://images.unsplash.com/photo-1665062173657-3912ae7991e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwc2VuaW9yJTIwY2l0aXplbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDU3MTc3MHww&ixlib=rb-4.1.0&q=85";

const IMG_ENVIRONMENT =
  "https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const CATEGORY_ARTICLES = {
  housing: {
    slug: "housing",
    label: "Housing & Shelter",
    title: "Building Safe Homes, Restoring Hope",
    overline: "CATEGORY · HOUSING",
    location: "Global · Social Pulse programs",
    hero_image: IMG_HOUSING,
    intro:
      "At Social Pulse, we believe that a safe home is more than just a place to live — it is the foundation for health, education, security, dignity, and opportunity. Millions of families around the world live in unsafe, damaged, or inadequate housing, making it difficult to build stable and productive lives.",
    lead:
      "Our housing initiatives focus on helping vulnerable families create homes that are safe, healthy, and resilient. Whether rebuilding after a natural disaster, repairing an aging structure, or constructing a new home for a family in need, every project is designed to improve quality of life and strengthen communities.",
    sections: [
      {
        heading: "Why housing matters",
        body: [
          "A secure home provides protection from the elements, reduces illness, improves children's ability to study, increases personal safety, and gives families a place where they can thrive. Strong housing also helps communities become more resilient during floods, storms, fires, and other disasters.",
          "When families have access to safe housing, they are better able to invest in education, employment, health, and their future.",
        ],
      },
      {
        heading: "Building new homes",
        body: [
          "Many families simply do not have adequate shelter. We help design and build affordable, durable homes using locally available materials whenever possible. Homes are built to withstand local weather conditions while remaining practical and sustainable.",
          "We will be providing names and website links of several organizations helping poor and needy people to get safe and secure houses around the world.",
        ],
      },
      {
        heading: "Home repairs and restoration",
        body: [
          "Many existing homes can be restored rather than replaced. We assist families whose homes have been damaged by:",
        ],
        list: [
          "Windstorms",
          "Hurricanes and cyclones",
          "Flooding",
          "Fires",
          "Earthquakes",
          "Landslides",
          "Aging structures",
          "General deterioration",
        ],
        outro:
          "Repairs often allow families to remain safely in their communities without the cost of complete reconstruction.",
      },
      {
        heading: "Roofing",
        body: ["A solid roof protects everything beneath it. Our roofing projects include:"],
        list: [
          "New roof installation",
          "Roof repairs",
          "Storm damage restoration",
          "Leak prevention",
          "Reinforced roofing for severe weather",
          "Insulation improvements where appropriate",
        ],
      },
      {
        heading: "Strong foundations and walls",
        body: ["Homes are only as strong as their structure. Projects may include:"],
        list: [
          "Foundation repairs",
          "Reinforcing walls",
          "Concrete block construction",
          "Brick construction",
          "Structural strengthening",
          "Earthquake-resistant improvements where appropriate",
          "Weather-resistant building techniques",
        ],
      },
      {
        heading: "Plumbing systems",
        body: ["Clean water inside the home is essential for health. We support:"],
        list: [
          "New plumbing installation",
          "Plumbing repairs",
          "Kitchen water systems",
          "Bathroom plumbing",
          "Water filtration connections",
          "Drainage improvements",
          "Leak detection and repair",
        ],
      },
      {
        heading: "Electrical systems",
        body: [
          "Safe electricity improves education, healthcare, communication, and economic opportunity. Our work includes:",
        ],
        list: [
          "Electrical wiring",
          "Safe electrical upgrades",
          "Circuit protection",
          "Indoor and outdoor lighting",
          "Energy-efficient lighting",
          "Electrical safety inspections",
          "Repairs after storm damage",
        ],
      },
      {
        heading: "Solar energy solutions",
        body: [
          "Many rural communities have limited or unreliable electricity. Solar solutions can provide dependable, renewable energy for:",
        ],
        list: [
          "Home lighting",
          "Phone charging",
          "Refrigeration",
          "Water pumping",
          "Schools",
          "Clinics",
          "Community centers",
          "Small businesses",
        ],
        outro:
          "Projects range from simple household solar kits to larger community solar installations.",
      },
      {
        heading: "Water supply systems",
        body: [
          "Access to clean water changes lives. We help communities develop reliable water systems including:",
        ],
        list: [
          "Household water connections",
          "Community water systems",
          "Rainwater harvesting",
          "Water purification systems",
          "Water storage solutions",
          "Distribution pipelines",
          "Gravity-fed water systems where suitable",
        ],
      },
      {
        heading: "Wells, pumps, and water storage",
        body: ["Reliable access to water is essential every day. Projects may include:"],
        list: [
          "Digging new wells",
          "Repairing existing wells",
          "Installing hand pumps",
          "Electric water pumps",
          "Solar-powered pumps",
          "Water storage tanks",
          "Elevated water towers",
          "Boreholes",
          "Pump maintenance",
        ],
      },
      {
        heading: "Sanitation and waste management",
        body: [
          "Proper sanitation protects families from disease and improves public health. Programs include:",
        ],
        list: [
          "Household waste disposal systems",
          "Community waste collection",
          "Drainage improvements",
          "Composting systems",
          "Recycling initiatives",
          "Safe wastewater management",
        ],
      },
      {
        heading: "Toilets and hygiene facilities",
        body: ["Many families still lack access to safe sanitation. We help build:"],
        list: [
          "Household toilets",
          "Community toilets",
          "Accessible toilets",
          "School sanitation facilities",
          "Handwashing stations",
          "Hygiene education programs",
        ],
      },
      {
        heading: "Septic systems",
        body: [
          "Proper wastewater treatment protects both people and the environment. Projects include:",
        ],
        list: [
          "New septic tanks",
          "Septic tank repairs",
          "Drain fields",
          "Wastewater management improvements",
          "Inspection and maintenance",
        ],
      },
      {
        heading: "Fencing and security",
        body: ["Families deserve to feel safe in their homes. We assist with:"],
        list: [
          "Property fencing",
          "Gates",
          "Livestock protection",
          "Child-safe play areas",
          "Boundary improvements",
          "Security lighting",
          "Community safety enhancements",
        ],
      },
      {
        heading: "Disaster recovery",
        body: [
          "Natural disasters can destroy years of hard work in just a few hours. Social Pulse responds by helping families recover through:",
        ],
        list: [
          "Emergency shelter",
          "Temporary housing",
          "Home repairs",
          "Rebuilding homes",
          "Replacing damaged roofs",
          "Restoring water systems",
          "Restoring electricity",
          "Community rebuilding projects",
        ],
        outro:
          "Our goal is not only to restore homes but also to help communities become more resilient against future disasters.",
      },
      {
        heading: "Building sustainable communities",
        body: [
          "Housing is only one part of creating thriving communities. We also encourage projects that improve neighborhoods and quality of life, including:",
        ],
        list: [
          "Community centers",
          "Roads and pathways",
          "Drainage systems",
          "Tree planting and landscaping",
          "Community gardens",
          "Playgrounds",
          "Street lighting",
          "Accessibility improvements for elderly and disabled residents",
          "Fire safety improvements",
          "Disaster preparedness planning",
        ],
      },
      {
        heading: "Helping families become self-reliant",
        body: [
          "Whenever possible, we work alongside homeowners and local communities rather than simply building for them. Community participation helps reduce costs, develops valuable construction skills, creates local employment opportunities, and strengthens a sense of ownership and pride.",
          "By partnering with local builders, volunteers, businesses, and community leaders, we help ensure that every project has a lasting impact.",
        ],
      },
      {
        heading: "How you can help",
        body: [
          "Every contribution helps provide a family with something many people take for granted — a safe place to call home. You can support our mission by:",
        ],
        list: [
          "Sponsoring a home-building project",
          "Funding home repairs",
          "Donating building materials",
          "Supporting clean water projects",
          "Funding solar energy installations",
          "Providing tools and equipment",
          "Volunteering construction skills",
          "Partnering as a business or organization",
          "Donating to disaster recovery efforts",
          "Sharing our mission with others",
        ],
        outro:
          "Together, we can build more than houses — we can build healthier families, stronger communities, and brighter futures. A safe home is where hope begins.",
      },
    ],
    cta: {
      label: "Sponsor a housing project",
      href: "/housing",
      secondary_label: "Volunteer construction skills",
      secondary_href: "/opportunities?category=housing",
    },
    closing_heading: "A safe home is where hope begins.",
    closing_body:
      "Sponsor a build, fund a repair, or volunteer your skills. Every contribution keeps a family safer, healthier, and closer to the life they deserve.",
  },
  food: {
    slug: "food",
    label: "Food & Basics",
    title: "Nourishing Lives, Ending Hunger",
    overline: "CATEGORY · FOOD & BASICS",
    location: "Global · Social Pulse programs",
    hero_image: IMG_FOOD,
    intro:
      "At Social Pulse, we believe that access to nutritious food is a basic human right. Every person deserves enough healthy food to live with dignity, maintain good health, and build a better future.",
    lead:
      "Yet millions of children, families, and elderly individuals around the world struggle with hunger every day. Many communities face food insecurity because of poverty, unemployment, natural disasters, conflict, rising food prices, or limited access to fresh and nutritious foods. Our mission is not only to provide immediate food assistance but also to help families become food secure through education, sustainable agriculture, nutrition, and community support.",
    sections: [
      {
        heading: "Why food matters",
        body: ["Good nutrition affects every part of life. Nutritious food helps:"],
        list: [
          "Children grow and develop properly",
          "Improve learning and school performance",
          "Strengthen immune systems",
          "Reduce disease and illness",
          "Support healthy pregnancies",
          "Increase energy and productivity",
          "Improve mental health and emotional well-being",
          "Help families break the cycle of poverty",
        ],
        outro: "A healthy community begins with healthy people.",
      },
      {
        heading: "Social Pulse: connecting communities through food",
        body: [
          "Social Pulse is proud to be part of the Social Pulse initiative under the Social Pulse umbrella — a growing movement dedicated to reducing food waste, improving food access, and connecting communities through technology, collaboration, and compassion.",
          "Social Pulse brings together consumers, farmers, fishermen, grocery stores, restaurants, wholesalers, food producers, charities, and volunteers on a single community-driven platform. By helping surplus food reach families instead of landfills, Social Pulse supports local economies, reduces environmental impact, and helps ensure that nutritious food reaches those who need it most. The platform promotes smarter shopping, responsible food management, and a more sustainable food ecosystem where everyone can play a role in reducing hunger and food waste.",
          "The vision behind Social Pulse comes from Chef Thomas (Tom) Vellaringattu, a chef, entrepreneur, and humanitarian based in Portland, Oregon, USA. Inspired by the belief that food has the power to bring people together and improve lives, Chef Tom founded Social Pulse to create innovative solutions that address hunger, food waste, affordability, and community well-being. His mission is simple yet powerful: to help people shop smarter, eat better, waste less, and ensure that good food reaches every table instead of every landfill.",
          "Through the partnership between Social Pulse and Social Pulse, we are building a future where communities work together to eliminate unnecessary food waste, support local food producers, strengthen food security, and ensure that everyone has access to healthy, nutritious meals.",
          "Learn more about Social Pulse at https://foodpulse.net.",
        ],
      },
      {
        heading: "Emergency food assistance",
        body: [
          "Families facing emergencies often need immediate help. Social Pulse provides emergency food assistance through:",
        ],
        list: [
          "Free hot meals",
          "Family food baskets",
          "Grocery vouchers where available",
          "Food hampers",
          "Ready-to-eat meals",
          "Emergency disaster food relief",
          "Holiday meal assistance",
          "Community food distributions",
        ],
        outro: "Our goal is to ensure that no family goes hungry during difficult times.",
      },
      {
        heading: "Healthy groceries",
        body: [
          "Access to fresh and nutritious food is essential for long-term health. We help provide groceries including:",
        ],
        list: [
          "Fresh vegetables",
          "Fresh fruits",
          "Whole grains",
          "Rice",
          "Beans and lentils",
          "Pasta",
          "Flour",
          "Cooking oils",
          "Dairy products",
          "Eggs",
          "Meat",
          "Poultry",
          "Fish",
          "Plant-based protein options",
          "Nuts and seeds",
          "Herbs and spices",
          "Baby food",
          "Infant formula when appropriate",
          "Staple household food items",
        ],
        outro:
          "Whenever possible, we purchase locally to support farmers and local businesses.",
      },
      {
        heading: "Community food banks and food pantries",
        body: [
          "Food banks play an important role in supporting families throughout the year. We help establish and support:",
        ],
        list: [
          "Community food pantries",
          "Food banks",
          "Mobile food distribution programs",
          "School food programs",
          "Senior nutrition programs",
          "Community refrigerators",
          "Emergency food storage facilities",
        ],
        outro: "These programs ensure food is available where it is needed most.",
      },
      {
        heading: "Food donations",
        body: ["Every donation helps feed someone in need. We encourage donations of:"],
        list: [
          "Non-perishable foods",
          "Fresh produce",
          "Frozen foods",
          "Dairy products",
          "Baby food",
          "School snacks",
          "Community garden harvests",
          "Restaurant surplus food",
          "Grocery store surplus food",
          "Farm produce",
          "Prepared meals for shelters and community kitchens",
        ],
        outro:
          "We work with businesses, farms, restaurants, supermarkets, schools, and community organizations to safely distribute excess food that might otherwise go to waste.",
      },
      {
        heading: "Community kitchens",
        body: [
          "Community kitchens bring people together while addressing hunger. Programs may include:",
        ],
        list: [
          "Free meal programs",
          "Soup kitchens",
          "Community cooking events",
          "Disaster relief kitchens",
          "Mobile meal services",
          "School meal preparation",
          "Senior meal services",
        ],
        outro:
          "These kitchens also provide opportunities for volunteers to serve their communities.",
      },
      {
        heading: "Refrigeration and food storage",
        body: [
          "Many families lose valuable food because they lack proper storage. We help provide:",
        ],
        list: [
          "Refrigerators",
          "Freezers",
          "Cool storage containers",
          "Food storage shelves",
          "Airtight food containers",
          "Solar-powered refrigeration where electricity is limited",
        ],
        outro: "Proper storage reduces waste and improves food safety.",
      },
      {
        heading: "Cooking equipment",
        body: [
          "Preparing healthy meals requires basic kitchen equipment. Our assistance may include:",
        ],
        list: [
          "Cooking stoves",
          "Energy-efficient cookstoves",
          "Solar cookers",
          "Gas or electric stoves",
          "Pots and pans",
          "Cooking utensils",
          "Plates and bowls",
          "Eating utensils",
          "Water kettles",
          "Food preparation tools",
        ],
        outro: "Safe cooking equipment reduces health risks and improves family nutrition.",
      },
      {
        heading: "Clean water",
        body: [
          "Clean water is essential for preparing safe food and maintaining good health. Our water initiatives include:",
        ],
        list: [
          "Safe drinking water",
          "Water purification systems",
          "Water filters",
          "Community wells",
          "Rainwater harvesting",
          "Water storage tanks",
          "Safe food preparation education",
        ],
        outro: "Without clean water, nutritious food alone cannot prevent disease.",
      },
      {
        heading: "Nutrition education",
        body: [
          "Knowing what to eat is just as important as having food. Our nutrition programs teach families about:",
        ],
        list: [
          "Balanced diets",
          "Healthy meal planning",
          "Child nutrition",
          "Infant feeding",
          "Maternal nutrition",
          "Healthy aging",
          "Reading food labels",
          "Portion sizes",
          "Reducing sugar and salt",
          "Affordable healthy eating",
          "Preventing malnutrition",
        ],
        outro:
          "Education helps families make healthier choices with the resources they have.",
      },
      {
        heading: "Vitamins and micronutrients",
        body: [
          "Many people suffer from hidden hunger caused by vitamin and mineral deficiencies. Where appropriate, programs may provide or promote access to:",
        ],
        list: [
          "Multivitamins",
          "Iron supplements",
          "Vitamin A",
          "Vitamin D",
          "Calcium",
          "Zinc",
          "Folic acid",
          "Prenatal nutrition",
          "Nutrition screening",
        ],
        outro:
          "These efforts support healthy growth, stronger immune systems, and better overall health.",
      },
      {
        heading: "Cooking lessons",
        body: [
          "Preparing nutritious meals does not have to be expensive. Our cooking education includes:",
        ],
        list: [
          "Healthy cooking classes",
          "Low-cost meal preparation",
          "Food safety",
          "Safe food storage",
          "Cooking with local ingredients",
          "Reducing food waste",
          "Traditional recipes",
          "Nutrition for children",
          "Budget-friendly shopping",
          "Meal planning",
        ],
        outro:
          "Practical cooking skills help families stretch their food budgets while improving nutrition.",
      },
      {
        heading: "Reducing food waste",
        body: [
          "Nearly one-third of the world's food is wasted while millions remain hungry. Social Pulse promotes responsible food use by teaching:",
        ],
        list: [
          "Proper food storage",
          "Meal planning",
          "Creative use of leftovers",
          "Composting food scraps",
          "Safe food preservation",
          "Seasonal eating",
          "Responsible purchasing",
          "Donation of excess food",
        ],
        outro: "Reducing waste benefits both people and the environment.",
      },
      {
        heading: "Kitchen gardens",
        body: [
          "Even a small garden can provide fresh, healthy food. We encourage families, schools, and communities to grow:",
        ],
        list: [
          "Tomatoes",
          "Leafy greens",
          "Peppers",
          "Herbs",
          "Beans",
          "Carrots",
          "Onions",
          "Sweet potatoes",
          "Fruit trees",
          "Native vegetables",
        ],
        outro:
          "Programs may include garden planning, seeds and seedlings, gardening tools, composting, rainwater irrigation, raised garden beds, and organic gardening techniques. Kitchen gardens improve nutrition, reduce food costs, and promote self-reliance.",
      },
      {
        heading: "Community agriculture",
        body: [
          "Long-term food security begins with local food production. Social Pulse supports:",
        ],
        list: [
          "Community gardens",
          "Small family farms",
          "School gardens",
          "Greenhouses",
          "Urban farming",
          "Hydroponic gardens",
          "Fruit orchards",
          "Seed banks",
          "Farmer training",
          "Sustainable agriculture",
          "Soil conservation",
          "Climate-smart farming practices",
        ],
        outro: "Strong local agriculture helps communities become more resilient.",
      },
      {
        heading: "Feeding children",
        body: [
          "Children should never have to learn on an empty stomach. We support initiatives such as:",
        ],
        list: [
          "School breakfast programs",
          "School lunches",
          "Weekend food packs",
          "Summer meal programs",
          "Nutrition education",
          "Early childhood feeding programs",
          "Meals for orphanages and children's homes",
        ],
        outro: "Healthy children are better prepared to learn, grow, and succeed.",
      },
      {
        heading: "Helping seniors and vulnerable families",
        body: [
          "Older adults, people with disabilities, and vulnerable families often face unique challenges in accessing nutritious food. Our programs may include:",
        ],
        list: [
          "Home-delivered meals",
          "Grocery assistance",
          "Special dietary support",
          "Mobility-friendly food deliveries",
          "Caregiver support",
          "Nutrition counseling",
        ],
        outro: "Every person deserves access to healthy food regardless of age or ability.",
      },
      {
        heading: "Building food security",
        body: [
          "Our vision goes beyond feeding people today. We aim to help communities become self-sufficient by improving local food systems, supporting farmers, reducing waste, encouraging home food production, and providing practical education.",
          "When families have reliable access to nutritious food, children thrive, adults are healthier, communities become stronger, and hope grows.",
        ],
      },
      {
        heading: "How you can help",
        body: ["You can make a lasting difference by:"],
        list: [
          "Donating food",
          "Sponsoring meals",
          "Supporting community kitchens",
          "Funding grocery assistance",
          "Providing refrigerators or cooking equipment",
          "Supporting nutrition education",
          "Donating seeds and gardening tools",
          "Volunteering at food distribution events",
          "Partnering as a farm, grocery store, restaurant, or food producer",
          "Supporting sustainable agriculture projects",
        ],
        outro:
          "Together, we can help ensure that every family has enough nutritious food to live healthy, productive, and joyful lives. Because no one should have to wonder where their next meal will come from.",
      },
    ],
    cta: {
      label: "Support food & meal programs",
      href: "/find-help?category=food",
      secondary_label: "Volunteer at a community kitchen",
      secondary_href: "/opportunities?category=food",
    },
    closing_heading: "No one should wonder where their next meal will come from.",
    closing_body:
      "Donate food, sponsor a meal, or volunteer at a community kitchen. Small actions add up — together we can help every family live healthy, productive, and joyful lives.",
  },
  education: {
    slug: "education",
    label: "Education & Supplies",
    title: "Empowering Minds, Transforming Futures",
    overline: "CATEGORY · EDUCATION",
    location: "Global · Social Pulse programs",
    hero_image: IMG_EDUCATION,
    intro:
      "At Social Pulse, we believe that education is one of the most powerful tools for breaking the cycle of poverty and creating lasting change. Every child, teenager, and adult deserves the opportunity to learn, develop new skills, and pursue their dreams regardless of their financial circumstances or where they were born.",
    lead:
      "Millions of children around the world are unable to attend school because they lack books, school supplies, transportation, proper nutrition, or even a safe place to study. Others leave school early because their families cannot afford educational expenses or because they need to work to support their households. Our mission is to remove these barriers by providing educational resources, financial assistance, mentoring, and opportunities that help students succeed from early childhood through higher education and lifelong learning.",
    sections: [
      {
        heading: "Why education matters",
        body: [
          "Education opens doors to opportunity and creates stronger families, healthier communities, and more prosperous nations. Quality education helps people:",
        ],
        list: [
          "Escape poverty",
          "Find meaningful employment",
          "Improve health and well-being",
          "Build confidence and leadership skills",
          "Make informed decisions",
          "Support their families",
          "Contribute to their communities",
          "Create brighter futures for the next generation",
        ],
        outro:
          "When one child receives an education, the benefits often extend to an entire family and community.",
      },
      {
        heading: "Access to quality education",
        body: [
          "Every child deserves the chance to attend school and learn in a safe, supportive environment. Social Pulse works to increase access to education by helping families overcome financial and practical obstacles that prevent children from staying in school.",
        ],
      },
      {
        heading: "Books and learning materials",
        body: ["Books inspire curiosity, creativity, and lifelong learning. We help provide:"],
        list: [
          "New books",
          "Gently used books",
          "Textbooks",
          "Children's storybooks",
          "Reference books",
          "Dictionaries",
          "Library books",
          "Educational workbooks",
          "Digital books and e-books",
          "Reading materials for adults",
          "Books in local languages",
          "Braille books and large-print materials where needed",
        ],
        outro:
          "We also encourage community book drives and the creation of small libraries in schools and neighborhoods.",
      },
      {
        heading: "School supplies",
        body: [
          "Many students cannot fully participate in school without basic supplies. Our programs may provide:",
        ],
        list: [
          "Backpacks",
          "Notebooks",
          "Pens and pencils",
          "Erasers",
          "Crayons",
          "Colored pencils",
          "Markers",
          "Rulers",
          "Calculators",
          "Scissors",
          "Glue",
          "Folders",
          "Binders",
          "Paper",
          "Art supplies",
          "Scientific calculators",
          "Classroom materials for teachers",
        ],
        outro: "Every student deserves the tools needed to learn and succeed.",
      },
      {
        heading: "School uniforms and clothing",
        body: [
          "In many countries, school uniforms are required but unaffordable for some families. We help provide:",
        ],
        list: [
          "School uniforms",
          "Shoes",
          "Socks",
          "Jackets",
          "Rain gear — umbrellas, raincoats, etc.",
          "Sports clothing",
          "Winter clothing where needed",
        ],
        outro:
          "Providing proper clothing helps children attend school with confidence and dignity.",
      },
      {
        heading: "Tutoring and academic support",
        body: [
          "Some students need additional help to reach their full potential. Our tutoring programs may include:",
        ],
        list: [
          "Reading support",
          "Writing assistance",
          "Mathematics tutoring",
          "Science tutoring",
          "Language learning",
          "Computer literacy",
          "Homework assistance",
          "Test preparation",
          "College entrance exam preparation",
          "Online tutoring",
          "Small-group learning sessions",
        ],
        outro:
          "Personalized support helps students build confidence and improve academic performance.",
      },
      {
        heading: "Scholarships",
        body: [
          "Financial challenges should never prevent someone from receiving an education. Scholarship opportunities may include:",
        ],
        list: [
          "Primary school support",
          "Secondary school scholarships",
          "College and university scholarships",
          "Technical and vocational training",
          "Trade schools",
          "Graduate education",
          "Adult education",
          "Continuing education",
          "Professional certification programs",
        ],
        outro:
          "Scholarships may cover tuition, books, transportation, housing, meals, or other educational expenses.",
      },
      {
        heading: "Mentoring",
        body: [
          "Students benefit greatly from positive role models. Mentoring programs help young people with:",
        ],
        list: [
          "Career exploration",
          "Goal setting",
          "Study habits",
          "Time management",
          "Leadership development",
          "Personal growth",
          "College planning",
          "Resume writing",
          "Interview preparation",
          "Financial literacy",
          "Life skills",
          "Building confidence",
        ],
        outro: "A caring mentor can make a lasting difference in a student's life.",
      },
      {
        heading: "Technology for learning",
        body: [
          "Technology has become an essential part of modern education. Social Pulse supports access to:",
        ],
        list: [
          "Laptop computers",
          "Tablets",
          "Desktop computers",
          "Educational software",
          "Internet access",
          "Wi-Fi hotspots",
          "Computer labs",
          "Online learning platforms",
          "Digital classrooms",
          "Educational videos",
        ],
        outro:
          "Technology helps students connect with learning opportunities regardless of their location.",
      },
      {
        heading: "Solar lamps for evening study",
        body: [
          "Many students live in communities with limited or unreliable electricity. Simple solar-powered lighting allows students to:",
        ],
        list: [
          "Study safely after sunset",
          "Complete homework",
          "Read books",
          "Prepare for exams",
          "Reduce dependence on candles or kerosene lamps",
        ],
        outro:
          "Reliable lighting extends learning opportunities while improving safety and reducing household costs.",
      },
      {
        heading: "School meals and nutrition",
        body: ["A hungry child cannot learn effectively. We support programs that provide:"],
        list: [
          "School breakfasts",
          "Nutritious lunches",
          "Healthy snacks",
          "Fresh fruit",
          "Sandwiches",
          "Salads",
          "Dairy products",
          "Clean drinking water",
        ],
        outro: "Good nutrition improves concentration, attendance, and academic performance.",
      },
      {
        heading: "Lunch boxes and water bottles",
        body: [
          "Small items can make a big difference in a student's daily experience. We provide:",
        ],
        list: [
          "Reusable lunch boxes",
          "Insulated food containers",
          "Refillable water bottles",
          "Healthy food storage containers",
        ],
        outro: "These simple resources encourage healthier eating habits and reduce waste.",
      },
      {
        heading: "Transportation assistance",
        body: [
          "Distance and transportation costs often prevent children from attending school. Our transportation support may include:",
        ],
        list: [
          "School bus programs",
          "Bus passes",
          "Train passes",
          "Bicycle donations",
          "Bicycle repairs",
          "Community transportation services",
          "Fuel assistance",
          "Safe walking initiatives",
        ],
        outro: "Reliable transportation increases school attendance and reduces dropout rates.",
      },
      {
        heading: "Financial assistance",
        body: [
          "Education involves many hidden costs beyond tuition. Financial assistance may help cover:",
        ],
        list: [
          "School fees",
          "Registration fees",
          "Examination fees",
          "Transportation",
          "School meals",
          "Uniforms",
          "Books",
          "School supplies",
          "Technology",
          "Internet access",
          "Graduation expenses",
        ],
        outro: "Our goal is to remove financial barriers that prevent learning.",
      },
      {
        heading: "Early childhood education",
        body: [
          "The earliest years are critical for lifelong development. Programs may include:",
        ],
        list: [
          "Preschool support",
          "Early literacy",
          "Learning through play",
          "Parent education",
          "Children's books",
          "Educational toys",
          "Developmental screening",
          "School readiness programs",
        ],
        outro: "Strong beginnings create stronger futures.",
      },
      {
        heading: "Adult education and literacy",
        body: ["Learning should never stop. Social Pulse supports adults through:"],
        list: [
          "Literacy programs",
          "English language classes",
          "Adult basic education",
          "GED preparation",
          "Computer skills",
          "Financial literacy",
          "Parenting education",
          "Entrepreneurship — startups",
          "Citizenship preparation where appropriate",
        ],
        outro:
          "Education empowers adults to improve their own lives while supporting their families.",
      },
      {
        heading: "Vocational and technical training",
        body: [
          "Not every career requires a university degree. We encourage practical skills through training in fields such as:",
        ],
        list: [
          "Carpentry",
          "Plumbing",
          "Electrical work",
          "Construction",
          "Welding",
          "Agriculture",
          "Hospitality",
          "Culinary arts",
          "Tailoring",
          "Sewing",
          "Automotive repair",
          "Information technology",
          "Graphic design",
          "Healthcare support",
          "Entrepreneurship",
        ],
        outro: "Practical skills lead to employment, self-reliance, and economic growth.",
      },
      {
        heading: "Libraries and community learning centers",
        body: ["Learning extends beyond the classroom. We help establish and support:"],
        list: [
          "Community libraries",
          "Reading clubs",
          "Mobile libraries",
          "Learning centers",
          "Homework clubs",
          "After-school programs",
          "Computer labs",
          "Study spaces",
          "Community workshops",
        ],
        outro: "These spaces encourage lifelong learning for people of all ages.",
      },
      {
        heading: "Teacher support",
        body: [
          "Great teachers create great futures. Our programs may assist educators by providing:",
        ],
        list: [
          "Classroom supplies",
          "Teaching materials",
          "Professional development",
          "Technology resources",
          "Library materials",
          "Educational workshops",
          "Recognition and encouragement",
        ],
        outro: "Supporting teachers improves education for entire communities.",
      },
      {
        heading: "Special education and inclusive learning",
        body: [
          "Every learner deserves equal opportunities. We support inclusive education by helping provide:",
        ],
        list: [
          "Learning accommodations",
          "Assistive technology",
          "Accessible classrooms",
          "Braille materials",
          "Sign language resources",
          "Specialized tutoring",
          "Inclusive teaching practices",
        ],
        outro: "Education should be accessible to students of all abilities.",
      },
      {
        heading: "Life skills education",
        body: [
          "Success in life requires more than academic knowledge. We encourage learning in areas such as:",
        ],
        list: [
          "Communication skills",
          "Leadership",
          "Teamwork",
          "Problem-solving",
          "Critical thinking",
          "Financial literacy",
          "Health and hygiene",
          "Digital safety",
          "Environmental stewardship",
          "Conflict resolution",
          "Community service",
        ],
        outro: "These skills prepare young people for responsible and fulfilling lives.",
      },
      {
        heading: "Encouraging a love of learning",
        body: [
          "Education is not simply about passing exams — it is about inspiring curiosity, creativity, compassion, and a lifelong desire to learn. We encourage:",
        ],
        list: [
          "Reading challenges",
          "Science fairs",
          "Art and music programs",
          "Cultural exchanges",
          "Educational field trips",
          "STEM clubs",
          "Robotics clubs",
          "Debate teams",
          "Environmental projects",
          "Volunteer opportunities",
        ],
        outro:
          "Learning becomes most powerful when students are inspired to explore the world around them.",
      },
      {
        heading: "How you can help",
        body: ["You can help transform lives through education by:"],
        list: [
          "Donating new or used books",
          "Sponsoring a student's education",
          "Providing scholarships",
          "Donating school supplies",
          "Funding school meals",
          "Providing backpacks or uniforms",
          "Donating computers or tablets",
          "Supporting internet access",
          "Funding transportation assistance",
          "Becoming a tutor or mentor",
          "Supporting libraries and learning centers",
          "Volunteering your professional skills",
        ],
        outro:
          "Together, we can help remove the barriers to education and create opportunities that last a lifetime. Every book opened, every lesson learned, and every student encouraged brings us one step closer to a world filled with hope, opportunity, and joy.",
      },
    ],
    cta: {
      label: "Sponsor a student",
      href: "/find-help?category=education",
      secondary_label: "Become a tutor or mentor",
      secondary_href: "/opportunities?category=education",
    },
    closing_heading: "Education doesn't just change one life — it changes generations.",
    closing_body:
      "Sponsor a scholarship, donate books, or mentor a student. Every act of support opens a door for someone dreaming of a better future.",
  },
  medical: {
    slug: "medical",
    label: "Medical Care & Prescription",
    title: "Bringing Health, Healing, and Hope",
    overline: "CATEGORY · MEDICAL CARE",
    location: "Global · Social Pulse programs",
    hero_image: IMG_MEDICAL,
    intro:
      "At Social Pulse, we believe that every person deserves access to quality healthcare regardless of their income, age, location, or circumstances. Good health is the foundation upon which people build their families, education, careers, and communities.",
    lead:
      "Around the world, millions of people suffer because they cannot afford medical care, medications, transportation, surgeries, or even routine checkups. Others live in remote areas where healthcare services are difficult to reach, while many families struggle to care for loved ones during illness or recovery. Our mission is to remove these barriers by providing medical assistance, supporting healthcare providers, promoting preventive care, and helping individuals and families receive the treatment they need with dignity and compassion.",
    sections: [
      {
        heading: "Why healthcare matters",
        body: [
          "Access to healthcare saves lives. Quality medical care helps people:",
        ],
        list: [
          "Prevent disease",
          "Detect illnesses early",
          "Recover from injuries",
          "Manage chronic conditions",
          "Improve quality of life",
          "Reduce disability",
          "Protect mothers and babies",
          "Support healthy aging",
          "Live longer, healthier lives",
        ],
        outro: "Healthy individuals create stronger families and stronger communities.",
      },
      {
        heading: "Medical care assistance",
        body: [
          "Many people delay treatment simply because they cannot afford it. Social Pulse helps individuals access:",
        ],
        list: [
          "Medical consultations",
          "Primary healthcare",
          "Specialist appointments",
          "Hospital care",
          "Emergency medical assistance",
          "Preventive healthcare",
          "Diagnostic testing",
          "Medical screenings",
          "Community health clinics",
          "Telehealth services where available",
        ],
        outro:
          "Our goal is to ensure that financial hardship never becomes a barrier to essential healthcare.",
      },
      {
        heading: "Medicines and medical supplies",
        body: ["Essential medicines save lives every day. We help provide access to:"],
        list: [
          "Prescription medications",
          "Over-the-counter medicines",
          "First aid supplies",
          "Medical dressings",
          "Wound care products",
          "Antibiotics when medically prescribed",
          "Diabetes supplies",
          "Asthma medications",
          "Blood pressure medications",
          "Pain management medications",
          "Children's medicines",
          "Nutritional supplements when appropriate",
        ],
        outro: "We also work to improve access to pharmacies and medication assistance programs.",
      },
      {
        heading: "Collection and donation of medical supplies",
        body: [
          "Many usable medical supplies are discarded while healthcare facilities elsewhere face shortages. Social Pulse encourages the responsible collection and donation of surplus, unopened, or safely reusable medical equipment and supplies, subject to applicable regulations and quality standards. Examples include:",
        ],
        list: [
          "Wheelchairs",
          "Walkers",
          "Crutches",
          "Canes",
          "Hospital beds",
          "Shower chairs",
          "Oxygen equipment",
          "Blood pressure monitors",
          "Thermometers",
          "Medical gloves",
          "Bandages",
          "Mobility aids",
          "Baby and adult diapers",
          "Patient care equipment",
        ],
        outro: "These donations can greatly improve healthcare access in underserved communities.",
      },
      {
        heading: "Surgical assistance",
        body: [
          "Many families cannot afford life-changing or life-saving surgical procedures. Our programs may help support access to:",
        ],
        list: [
          "Emergency surgery",
          "Orthopedic surgery",
          "Cardiac surgery",
          "Cancer-related surgery",
          "Cataract surgery",
          "Cleft lip and palate surgery (e.g. Smiles International)",
          "Reconstructive surgery",
          "Pediatric surgery",
          "Hernia repair",
          "Burn treatment",
          "Post-operative care",
        ],
        outro:
          "Where possible, we collaborate with hospitals, volunteer medical teams, and charitable organizations.",
      },
      {
        heading: "Transportation to medical services",
        body: [
          "Transportation should never prevent someone from receiving healthcare. We help arrange or support transportation to:",
        ],
        list: [
          "Hospitals",
          "Medical clinics",
          "Doctor appointments",
          "Specialists",
          "Physical therapy",
          "Occupational therapy",
          "Speech therapy",
          "Pharmacies",
          "Diagnostic laboratories",
          "Imaging centers",
          "Dialysis centers",
          "Cancer treatment facilities",
          "Mental health providers",
          "Rehabilitation centers",
        ],
        outro: "Reliable transportation improves treatment outcomes and reduces missed appointments.",
      },
      {
        heading: "Patient support during hospital stays",
        body: [
          "Hospitalization can be emotionally and physically overwhelming. Our volunteers and caregivers may provide:",
        ],
        list: [
          "Hospital companionship",
          "Assistance communicating with healthcare teams",
          "Emotional support",
          "Reading and conversation",
          "Help with meals",
          "Assistance for elderly patients",
          "Family respite support",
          "Practical assistance during recovery",
        ],
        outro: "Sometimes simply having someone present makes a difficult experience easier.",
      },
      {
        heading: "Home visits and community care",
        body: [
          "Recovery often continues after leaving the hospital. Volunteers may visit patients at home to provide:",
        ],
        list: [
          "Friendly companionship",
          "Wellness check-ins",
          "Emotional encouragement",
          "Light household assistance",
          "Meal delivery",
          "Transportation assistance",
          "Prayer or spiritual support if requested",
          "Connections to community resources",
        ],
        outro: "These visits help reduce isolation and promote healing.",
      },
      {
        heading: "Nursing and home health support",
        body: [
          "Many patients require skilled care after illness or surgery. Programs may include:",
        ],
        list: [
          "Home nursing visits",
          "Wound care",
          "Medication reminders",
          "Blood pressure monitoring",
          "Diabetes management",
          "Elder care",
          "Rehabilitation support",
          "Mobility assistance",
          "Recovery monitoring",
          "Family caregiver education",
        ],
        outro:
          "Professional and volunteer caregivers can make recovery safer and more comfortable.",
      },
      {
        heading: "Medical equipment",
        body: [
          "Medical equipment often allows individuals to remain independent and improve their quality of life. Assistance may include:",
        ],
        list: [
          "Wheelchairs",
          "Walkers",
          "Hospital beds",
          "Hearing aids",
          "Eyeglasses",
          "Oxygen concentrators",
          "CPAP machines",
          "Nebulizers",
          "Mobility scooters",
          "Prosthetic limbs",
          "Orthopedic braces",
          "Patient lifts",
        ],
        outro: "Equipment donations can dramatically improve mobility and independence.",
      },
      {
        heading: "Vision care",
        body: [
          "Healthy vision supports education, employment, and daily living. Programs may provide:",
        ],
        list: [
          "Eye examinations",
          "Prescription glasses",
          "New & used frames for glasses",
          "Reading glasses",
          "Cataract screening",
          "Vision education",
          "Vision care referrals",
          "Supplies for blind or near-blind people (canes, hats, rechargeable beacons, etc.)",
        ],
        outro: "Restoring sight can transform lives.",
      },
      {
        heading: "Dental care",
        body: ["Oral health is an essential part of overall health. Support may include:"],
        list: [
          "Dental examinations",
          "Cleanings",
          "Fillings",
          "Tooth extractions",
          "Preventive dental education",
          "Children's dental care",
          "Dentures where appropriate",
        ],
        outro: "Good oral health improves nutrition, confidence, and overall well-being.",
      },
      {
        heading: "Maternal and child health",
        body: [
          "Healthy mothers give children the best possible start in life. Programs may support:",
        ],
        list: [
          "Prenatal care",
          "Postnatal care",
          "Safe childbirth education",
          "Infant health",
          "Childhood vaccinations",
          "Growth monitoring",
          "Nutrition counseling",
          "Breastfeeding support",
          "Parenting education",
        ],
        outro: "Investing in mothers and children strengthens entire communities.",
      },
      {
        heading: "Preventive healthcare",
        body: [
          "Preventing disease is often less costly and more effective than treating it. Our health education programs encourage:",
        ],
        list: [
          "Regular medical checkups",
          "Vaccinations",
          "Healthy nutrition",
          "Physical activity",
          "Handwashing and hygiene",
          "Safe drinking water",
          "Disease prevention",
          "Smoking cessation",
          "Responsible alcohol use",
          "Sun safety",
          "Health screenings",
        ],
        outro: "Healthy habits save lives.",
      },
      {
        heading: "Mental health and emotional well-being",
        body: [
          "Health includes emotional and psychological well-being. Social Pulse supports access to:",
        ],
        list: [
          "Counseling",
          "Therapy referrals",
          "Peer support groups",
          "Crisis support",
          "Grief counseling",
          "Stress management",
          "Family counseling",
          "Youth mental health programs",
          "Community wellness initiatives",
        ],
        outro: "Everyone deserves compassionate support during difficult times.",
      },
      {
        heading: "Caregiver support",
        body: [
          "Family caregivers often sacrifice their own health while caring for loved ones. We seek to support caregivers through:",
        ],
        list: [
          "Respite care",
          "Education and training",
          "Emotional support",
          "Resource referrals",
          "Support groups",
          "Home assistance",
          "Temporary caregiving assistance",
        ],
        outro: "Supporting caregivers helps entire families.",
      },
      {
        heading: "Childcare assistance",
        body: [
          "Parents facing illness often worry about caring for their children. Programs may provide:",
        ],
        list: [
          "Babysitting assistance",
          "Childcare during medical appointments",
          "Temporary family support",
          "After-school care",
          "Emergency childcare referrals",
        ],
        outro: "This support allows parents to focus on treatment and recovery.",
      },
      {
        heading: "Blood donation",
        body: [
          "One blood donation can help save multiple lives. Social Pulse encourages communities to:",
        ],
        list: [
          "Donate blood regularly",
          "Organize blood drives",
          "Learn about blood donation",
          "Support local blood banks",
          "Promote emergency blood availability",
        ],
        outro: "Giving blood is a simple act of generosity with life-saving impact.",
      },
      {
        heading: "Organ and tissue donation",
        body: [
          "Organ donation offers hope to people waiting for life-saving transplants. We promote education about:",
        ],
        list: [
          "Organ donation",
          "Tissue donation",
          "Cornea donation",
          "Bone marrow donation",
          "Living donation",
          "Donor registration",
          "Family discussions about donation",
        ],
        outro:
          "By increasing awareness, more lives can be saved through informed personal choices.",
      },
      {
        heading: "End-of-life care",
        body: [
          "Every person deserves compassion, dignity, and comfort at the end of life. Support may include:",
        ],
        list: [
          "Hospice care referrals",
          "Palliative care",
          "Emotional support",
          "Spiritual care when requested",
          "Family assistance",
          "Bereavement support",
          "Grief resources",
        ],
        outro:
          "Compassion during life's final chapter is one of the greatest gifts we can offer.",
      },
      {
        heading: "Funeral and cremation assistance",
        body: [
          "The loss of a loved one is emotionally and financially overwhelming. Where possible, Social Pulse may assist families with:",
        ],
        list: [
          "Funeral expenses",
          "Cremation assistance",
          "Memorial services",
          "Transportation arrangements",
          "Documentation guidance",
          "Grief counseling referrals",
        ],
        outro:
          "Helping families during times of loss allows them to focus on remembrance rather than financial hardship.",
      },
      {
        heading: "Community health education",
        body: ["Knowledge empowers people to protect their health. Educational programs may include:"],
        list: [
          "First aid training",
          "CPR certification",
          "Disease prevention",
          "Nutrition education",
          "Women's health",
          "Men's health",
          "Child safety",
          "Elder care",
          "Medication safety",
          "Emergency preparedness",
        ],
        outro: "Education helps communities become healthier and more resilient.",
      },
      {
        heading: "Disaster medical relief",
        body: [
          "Following disasters, healthcare needs often increase dramatically. Social Pulse may assist through:",
        ],
        list: [
          "Emergency medical clinics",
          "Mobile healthcare units",
          "Medical supply distribution",
          "First aid stations",
          "Disease prevention",
          "Mental health support",
          "Long-term recovery assistance",
        ],
        outro: "Our goal is to help communities recover safely and effectively.",
      },
      {
        heading: "Medical volunteers",
        body: [
          "Healthcare professionals and volunteers play a vital role in improving lives. We welcome:",
        ],
        list: [
          "Doctors",
          "Nurses",
          "Dentists",
          "Pharmacists",
          "Therapists",
          "Emergency medical personnel",
          "Counselors",
          "Medical students",
          "Public health professionals",
          "Community health workers",
        ],
        outro: "Their expertise brings healing to those who need it most.",
      },
      {
        heading: "Related focus areas",
        body: [
          "Our medical initiatives connect with several closely related areas that support whole-person health:",
        ],
        list: [
          "Mental Health",
          "Disability Support",
          "Maternal & Child Health",
          "Clean Water & Sanitation",
          "Disaster Response",
          "Healthy Aging",
          "Community Wellness",
        ],
      },
      {
        heading: "How you can help",
        body: ["You can support healthier communities by:"],
        list: [
          "Donating to medical assistance programs",
          "Donating medical equipment or eligible supplies",
          "Supporting surgeries for patients in need",
          "Sponsoring medications",
          "Volunteering as a healthcare professional",
          "Driving patients to appointments",
          "Visiting patients in hospitals or at home",
          "Donating blood regularly",
          "Registering as an organ donor where available",
          "Supporting hospice and caregiver programs",
          "Funding community health education",
          "Partnering with hospitals, clinics, pharmacies, and healthcare organizations",
        ],
        outro:
          "Together, we can help ensure that every person has the opportunity to receive compassionate, quality healthcare. Healing is more than treating illness — it is restoring hope, dignity, comfort, and the opportunity to live life to its fullest. Every act of care, whether large or small, has the power to change a life.",
      },
    ],
    cta: {
      label: "Sponsor a surgery or treatment",
      href: "/find-help?category=medical",
      secondary_label: "Volunteer medical skills",
      secondary_href: "/opportunities?category=medical",
    },
    closing_heading: "Healing is restoring hope, dignity, and the opportunity to live fully.",
    closing_body:
      "Donate to fund treatments, contribute medical supplies, drive a patient, or lend your professional skills. Every act of care can change a life.",
  },
  seniors: {
    slug: "seniors",
    label: "Senior Care & Childcare",
    title: "Honoring Lives with Care, Respect, and Compassion",
    overline: "CATEGORY · SENIORS",
    location: "Global · Social Pulse programs",
    hero_image: IMG_SENIORS,
    intro:
      "At Social Pulse, we believe that every older adult deserves to age with dignity, comfort, security, and purpose. Seniors have spent their lives building families, strengthening communities, sharing wisdom, and contributing to society. As they enter the later years of life, many face challenges that can make everyday living difficult.",
    lead:
      "Loneliness, declining health, limited mobility, financial hardship, and the loss of loved ones can greatly affect a senior's quality of life. Our mission is to provide practical assistance, meaningful companionship, and community support so that older adults can continue living independently, safely, and with joy.",
    sections: [
      {
        heading: "Why supporting seniors matters",
        body: [
          "The world's senior population is growing rapidly. Many older adults live alone or rely on fixed incomes that make it difficult to afford housing, food, transportation, healthcare, and daily necessities. Supporting seniors helps:",
        ],
        list: [
          "Improve physical and mental health",
          "Reduce loneliness and isolation",
          "Promote independence",
          "Prevent accidents and neglect",
          "Strengthen families and communities",
          "Preserve valuable life experiences and wisdom",
          "Improve overall quality of life",
        ],
        outro: "Every senior deserves to feel valued, respected, and connected.",
      },
      {
        heading: "Companionship and friendship",
        body: [
          "One of the greatest needs among seniors is simply having someone who cares. Social Pulse encourages volunteers to spend time with seniors through:",
        ],
        list: [
          "Friendly home visits",
          "Phone calls",
          "Video chats",
          "Reading books or newspapers together",
          "Playing games",
          "Sharing hobbies",
          "Listening to life stories",
          "Celebrating birthdays and holidays",
          "Walking together",
          "Writing letters and cards",
        ],
        outro: "Sometimes a simple conversation can brighten an entire day.",
      },
      {
        heading: "Transportation assistance",
        body: [
          "Reliable transportation helps seniors remain independent and active. We support transportation for:",
        ],
        list: [
          "Medical appointments",
          "Hospital visits",
          "Grocery shopping",
          "Pharmacy visits",
          "Religious services",
          "Community events",
          "Social gatherings",
          "Family visits",
          "Hair appointments",
          "Banking and essential errands",
        ],
        outro:
          "Transportation assistance reduces isolation and helps seniors access the services they need.",
      },
      {
        heading: "Grocery shopping and food assistance",
        body: [
          "Nutritious food is essential for healthy aging. Our programs may provide:",
        ],
        list: [
          "Grocery shopping assistance",
          "Home grocery delivery",
          "Fresh fruits and vegetables",
          "Dairy products",
          "Meat and poultry",
          "Healthy prepared meals",
          "Food vouchers",
          "Community food pantries",
          "Senior meal programs",
          "Emergency food assistance",
        ],
        outro: "No senior should have to choose between buying food and paying bills.",
      },
      {
        heading: "Medical support",
        body: [
          "Healthcare becomes increasingly important with age. Social Pulse may assist seniors with access to:",
        ],
        list: [
          "Medical appointments",
          "Prescription medications",
          "Medical equipment",
          "Hearing aids",
          "Eyeglasses",
          "Blood pressure monitors",
          "Diabetes supplies",
          "Walkers",
          "Wheelchairs",
          "Canes",
          "Home health services",
          "Rehabilitation support",
        ],
        outro:
          "Helping seniors receive timely medical care promotes healthier, more independent living.",
      },
      {
        heading: "Medication assistance",
        body: ["Managing multiple medications can be challenging. Support may include:"],
        list: [
          "Prescription assistance",
          "Medication reminders",
          "Pharmacy transportation",
          "Organizing medications safely",
          "Education about medication management",
          "Assistance with prescription costs",
        ],
        outro: "Proper medication management helps prevent complications and hospitalizations.",
      },
      {
        heading: "Home visits and safety checks",
        body: [
          "Regular visits provide peace of mind for seniors and their families. Volunteers and caregivers may assist with:",
        ],
        list: [
          "Wellness checks",
          "Friendly visits",
          "Safety inspections",
          "Emergency preparedness",
          "Smoke detector checks",
          "Fall prevention recommendations",
          "Seasonal home safety reviews",
          "Identifying repair needs",
        ],
        outro: "These visits help seniors remain safely in their own homes.",
      },
      {
        heading: "Home maintenance and repairs",
        body: ["Many seniors struggle with household maintenance. Our programs may assist with:"],
        list: [
          "Minor home repairs",
          "Plumbing repairs",
          "Electrical repairs",
          "Roof maintenance",
          "Yard work",
          "Lawn mowing",
          "Snow removal",
          "Gutter cleaning",
          "Fence repairs",
          "Painting",
          "Accessibility improvements",
          "Grab bar installation",
          "Wheelchair ramps",
        ],
        outro: "Safe homes reduce injuries and increase independence.",
      },
      {
        heading: "Affordable technology",
        body: [
          "Technology helps seniors stay connected with loved ones and access important services. We help provide access to:",
        ],
        list: [
          "Affordable mobile phones",
          "Smartphones",
          "Tablets",
          "Internet service",
          "Emergency alert devices",
          "Video calling equipment",
          "Large-button phones",
          "Hearing-friendly devices",
        ],
        outro: "Technology can reduce isolation and improve safety.",
      },
      {
        heading: "Technology education",
        body: [
          "Many seniors want to use technology but lack confidence. Our educational programs teach:",
        ],
        list: [
          "Smartphone basics",
          "Video calling",
          "Text messaging",
          "Email",
          "Internet safety",
          "Online banking awareness",
          "Telehealth appointments",
          "Social media safety",
          "Password protection",
          "Scam prevention",
          "Online shopping",
          "Digital privacy",
        ],
        outro:
          "Knowledge helps seniors remain connected while protecting themselves from fraud.",
      },
      {
        heading: "Scam and fraud awareness",
        body: [
          "Unfortunately, seniors are often targeted by scammers. Educational workshops may cover:",
        ],
        list: [
          "Telephone scams",
          "Email scams",
          "Online fraud",
          "Identity theft prevention",
          "Financial exploitation",
          "Investment scams",
          "Romance scams",
          "Charity fraud",
          "Reporting suspicious activity",
        ],
        outro: "Education is one of the best forms of protection.",
      },
      {
        heading: "Nutrition and healthy cooking",
        body: ["Healthy eating supports longer, healthier lives. Programs may include:"],
        list: [
          "Nutrition education",
          "Cooking classes",
          "Budget-friendly meal planning",
          "Special diets for medical conditions",
          "Healthy grocery shopping",
          "Food safety",
          "Hydration education",
        ],
        outro: "Good nutrition helps seniors maintain strength and independence.",
      },
      {
        heading: "Exercise and wellness",
        body: ["Regular physical activity improves health at every age. Activities may include:"],
        list: [
          "Walking groups",
          "Chair exercises",
          "Stretching",
          "Balance training",
          "Fall prevention exercises",
          "Yoga",
          "Tai Chi",
          "Swimming",
          "Gentle fitness classes",
        ],
        outro: "Exercise improves mobility, confidence, and overall well-being.",
      },
      {
        heading: "Mental health and emotional support",
        body: ["Growing older often brings significant life changes. Support may include:"],
        list: [
          "Emotional support",
          "Counseling referrals",
          "Grief support",
          "Peer support groups",
          "Memory cafés",
          "Stress management",
          "Social activities",
          "Volunteer companionship",
        ],
        outro: "No one should face life's challenges alone.",
      },
      {
        heading: "Memory care support",
        body: [
          "Families affected by dementia or Alzheimer's disease often need additional assistance. Programs may include:",
        ],
        list: [
          "Memory-friendly activities",
          "Caregiver education",
          "Safety planning",
          "Companion visits",
          "Respite care",
          "Resource referrals",
          "Support groups",
        ],
        outro: "Compassionate support improves quality of life for both seniors and caregivers.",
      },
      {
        heading: "Pet assistance",
        body: [
          "For many seniors, pets provide companionship and emotional comfort. We may assist with:",
        ],
        list: [
          "Pet food",
          "Veterinary assistance",
          "Dog walking",
          "Pet grooming",
          "Temporary pet care during hospitalization",
          "Pet transportation",
          "Adoption support when needed",
        ],
        outro: "Helping seniors care for beloved pets also supports their emotional well-being.",
      },
      {
        heading: "Financial guidance",
        body: ["Living on a fixed income can be challenging. Educational programs may cover:"],
        list: [
          "Budgeting",
          "Benefits and assistance programs",
          "Utility assistance",
          "Tax preparation",
          "Financial planning",
          "Avoiding financial scams",
          "Estate planning resources",
        ],
        outro: "Helping seniors understand available resources can ease financial stress.",
      },
      {
        heading: "Lifelong learning",
        body: [
          "Learning does not end with retirement. We encourage seniors to continue growing through:",
        ],
        list: [
          "Educational classes",
          "Arts and crafts",
          "Music programs",
          "Computer courses",
          "Language classes",
          "Gardening",
          "Book clubs",
          "Local history groups",
          "Volunteer opportunities",
        ],
        outro: "Lifelong learning keeps minds active and spirits engaged.",
      },
      {
        heading: "Social and community activities",
        body: [
          "Strong social connections contribute to healthy aging. Programs may include:",
        ],
        list: [
          "Community lunches",
          "Coffee mornings",
          "Game days",
          "Movie afternoons",
          "Holiday celebrations",
          "Cultural events",
          "Gardening clubs",
          "Choirs",
          "Volunteer projects",
          "Intergenerational activities with youth",
        ],
        outro: "Meaningful relationships enrich every stage of life.",
      },
      {
        heading: "Respite care for families",
        body: [
          "Caring for an aging loved one can be rewarding but demanding. We support family caregivers through:",
        ],
        list: [
          "Temporary respite care",
          "Volunteer companions",
          "Caregiver education",
          "Support groups",
          "Resource referrals",
          "Emotional support",
        ],
        outro: "Helping caregivers also helps seniors receive better care.",
      },
      {
        heading: "Emergency preparedness",
        body: [
          "Older adults may need additional support during emergencies. Programs may assist with:",
        ],
        list: [
          "Emergency contact plans",
          "Medical information cards",
          "Disaster preparedness kits",
          "Backup medication plans",
          "Wellness checks during severe weather",
          "Evacuation planning",
        ],
        outro: "Preparedness improves safety and peace of mind.",
      },
      {
        heading: "End-of-life support",
        body: [
          "Every person deserves dignity, compassion, and respect during life's final chapter. Our services may include:",
        ],
        list: [
          "Hospice support referrals",
          "Companionship",
          "Family support",
          "Spiritual care upon request",
          "Bereavement resources",
          "Funeral and memorial assistance referrals",
        ],
        outro:
          "Supporting families through difficult times is an important expression of compassion.",
      },
      {
        heading: "Volunteer opportunities",
        body: [
          "Volunteers make an incredible difference in the lives of seniors. You can help by:",
        ],
        list: [
          "Visiting seniors",
          "Driving to appointments",
          "Delivering groceries or meals",
          "Teaching technology",
          "Helping with home maintenance",
          "Reading aloud",
          "Walking pets",
          "Making friendly phone calls",
          "Organizing community activities",
          "Assisting caregivers",
        ],
        outro: "Even one hour of your time can make someone's day brighter.",
      },
      {
        heading: "How you can help",
        body: ["You can support seniors by:"],
        list: [
          "Sponsoring transportation services",
          "Sponsoring senior care living for the elderly",
          "Donating groceries",
          "Funding medical assistance",
          "Donating mobile phones or tablets",
          "Supporting home repair projects",
          "Providing mobility equipment",
          "Volunteering your time",
          "Donating pet food and supplies",
          "Organizing social activities",
          "Partnering with senior centers, healthcare providers, and community organizations",
        ],
        outro:
          "Together, we can help older adults enjoy their later years with dignity, independence, good health, meaningful relationships, and the support of a caring community. Because growing older should never mean growing alone. Every senior deserves to be valued, respected, and surrounded by kindness. Housing, nutrition, transportation, social connection, lifelong learning, financial security, and community support all work together to help seniors live fulfilling, independent lives — reflecting the core mission of Social Pulse: helping people of every age live with dignity, hope, and joy.",
      },
    ],
    cta: {
      label: "Sponsor senior care programs",
      href: "/find-help?category=seniors",
      secondary_label: "Visit or drive a senior",
      secondary_href: "/opportunities?category=seniors",
    },
    closing_heading: "Growing older should never mean growing alone.",
    closing_body:
      "Visit a senior, drive them to an appointment, deliver groceries, or teach a smartphone skill. Even one hour of your time can brighten someone's day.",
  },
  environment: {
    slug: "environment",
    label: "Community & Environment",
    title: "Working Together for Cleaner, Safer, and Stronger Communities",
    overline: "CATEGORY · COMMUNITY & ENVIRONMENT",
    location: "Global · Social Pulse programs",
    hero_image: IMG_ENVIRONMENT,
    intro:
      "At Social Pulse, we believe that thriving communities begin with people who care for one another and for the environment they share. Every neighborhood, village, town, and city becomes a better place to live when residents work together to keep it clean, safe, welcoming, and sustainable.",
    lead:
      "Small acts of kindness — planting a tree, picking up litter, helping a neighbor, or reporting a safety concern — can create lasting positive change. By working together, we can build communities that are healthier, greener, safer, and more resilient for current and future generations.",
    sections: [
      {
        heading: "Why community involvement matters",
        body: [
          "Strong communities are built by people who take pride in where they live. Community involvement helps to:",
        ],
        list: [
          "Improve public health",
          "Reduce pollution",
          "Increase neighborhood safety",
          "Protect wildlife and natural habitats",
          "Build friendships and social connections",
          "Encourage civic responsibility",
          "Beautify neighborhoods",
          "Strengthen local economies",
          "Create safer spaces for children and families",
          "Leave a positive legacy for future generations",
        ],
        outro: "Everyone has a role to play in making their community a better place.",
      },
      {
        heading: "Tree planting",
        body: ["Trees are among nature's greatest gifts. They provide:"],
        list: [
          "Clean air",
          "Shade",
          "Cooler neighborhoods",
          "Wildlife habitat",
          "Reduced soil erosion",
          "Carbon storage",
          "Improved mental well-being",
          "Increased property values",
          "More beautiful communities",
        ],
        outro:
          "Social Pulse encourages individuals, schools, businesses, and organizations to participate in tree planting and long-term tree care.",
      },
      {
        heading: "Community gardens",
        body: [
          "Community gardens bring people together while producing healthy food. Our programs support:",
        ],
        list: [
          "Vegetable gardens",
          "Fruit gardens",
          "Herb gardens",
          "Pollinator gardens",
          "School gardens",
          "Senior gardens",
          "Accessible raised-bed gardens",
          "Composting",
          "Rainwater collection",
          "Volunteer gardening days",
        ],
        outro:
          "Gardens improve food security, promote healthy eating, and strengthen neighborhoods.",
      },
      {
        heading: "Sharing food and resources",
        body: [
          "Many families have items they no longer need, while others struggle to meet basic needs. We encourage communities to donate:",
        ],
        list: [
          "Fresh vegetables",
          "Fruits",
          "Homegrown produce",
          "Packaged food",
          "Clothing",
          "Household goods",
          "Furniture",
          "Books",
          "School supplies",
          "Medical equipment",
          "Toys",
          "Baby supplies",
        ],
        outro: "Sharing excess resources reduces waste while helping neighbors in need.",
      },
      {
        heading: "Community clean-up events",
        body: [
          "A clean neighborhood is healthier and more welcoming. Volunteer clean-up projects may include:",
        ],
        list: [
          "Street cleanups",
          "Park cleanups",
          "Disposing of dog waste by bagging or flicking from walkways and hiking trails",
          "Playground maintenance",
          "Sidewalk cleaning",
          "Trail cleanups",
          "Beach cleanups",
          "River cleanups",
          "Lake cleanups",
          "Pond restoration",
          "Storm drain cleaning",
          "Cleaning and repainting tagging and graffiti",
          "Neighborhood beautification",
        ],
        outro: "Working together builds pride and strengthens community spirit.",
      },
      {
        heading: "Protecting rivers, lakes, and oceans",
        body: [
          "Clean water benefits everyone. Social Pulse encourages volunteers to help:",
        ],
        list: [
          "Remove litter from rivers",
          "Clean lakeshores",
          "Protect wetlands",
          "Restore streams",
          "Remove invasive plants",
          "Protect aquatic wildlife",
          "Prevent pollution",
          "Educate communities about water conservation",
        ],
        outro: "Healthy waterways support healthier ecosystems and healthier communities.",
      },
      {
        heading: "Reducing waste",
        body: [
          "Reducing waste protects both the environment and future generations. We encourage:",
        ],
        list: [
          "Recycling",
          "Reusing household items",
          "Composting food waste",
          "Reducing plastic use",
          "Repairing instead of replacing",
          "Responsible disposal of electronics",
          "Hazardous waste collection",
          "Donation instead of disposal",
        ],
        outro: "Every small effort contributes to a cleaner planet.",
      },
      {
        heading: "Community recycling programs",
        body: [
          "Recycling conserves valuable natural resources. Programs may support collection of:",
        ],
        list: [
          "Paper",
          "Cardboard",
          "Plastic",
          "Glass",
          "Aluminum",
          "Steel",
          "Electronics",
          "Batteries",
          "Clothing",
          "Shopping bags, travel bags and backpacks",
          "Household textiles",
        ],
        outro: "Education helps communities recycle more effectively.",
      },
      {
        heading: "Reporting community concerns",
        body: [
          "Community members are often the first to notice problems that need attention. We encourage residents to responsibly report:",
        ],
        list: [
          "Illegal dumping",
          "Littering",
          "Graffiti",
          "Unsafe sidewalks",
          "Broken streetlights",
          "Damaged playground equipment",
          "Blocked drainage systems",
          "Water leaks",
          "Fallen trees",
          "Dangerous intersections",
          "Unsafe public spaces",
        ],
        outro: "Prompt reporting helps local authorities respond more quickly.",
      },
      {
        heading: "Road and infrastructure safety",
        body: ["Safe roads benefit everyone. Community members can help identify:"],
        list: [
          "Potholes (can do immediate temporary repairs to reduce accidents)",
          "Damaged sidewalks",
          "Broken traffic signs",
          "Faded crosswalks",
          "Malfunctioning traffic lights",
          "Blocked roads",
          "Road accidents and fires",
          "Unsafe bridges",
          "Unsafe debris blocking streets and roads",
          "Flooded streets",
        ],
        outro: "Reporting these issues can help prevent accidents and injuries.",
      },
      {
        heading: "Public sanitation",
        body: [
          "Clean public spaces improve health and quality of life. We encourage communities to report:",
        ],
        list: [
          "Overflowing trash bins",
          "Illegal waste dumping",
          "Sewage leaks",
          "Unsafe public toilets",
          "Standing water",
          "Drainage problems",
          "Pest infestations",
        ],
        outro: "Good sanitation reduces disease and protects public health.",
      },
      {
        heading: "Safe driving awareness",
        body: [
          "Road safety saves lives. Social Pulse promotes responsible driving by encouraging:",
        ],
        list: [
          "Obeying speed limits",
          "Avoiding distracted driving",
          "Respecting pedestrians",
          "Safe cycling practices",
          "Courtesy toward other drivers",
          "Reporting dangerous road conditions and unsafe driving",
          "Promoting road safety education",
        ],
        outro: "Every responsible driver helps create safer communities.",
      },
      {
        heading: "Trash collection and waste management",
        body: [
          "Proper waste disposal keeps neighborhoods clean and healthy. Projects may include:",
        ],
        list: [
          "Installing public trash receptacles",
          "Recycling stations",
          "Dog waste stations",
          "Regular waste collection",
          "Community compost bins",
          "Volunteer waste monitoring",
          "Public education campaigns",
        ],
        outro: "Proper waste management benefits everyone.",
      },
      {
        heading: "Caring for public spaces",
        body: ["Beautiful public spaces encourage community pride. Volunteers may help with:"],
        list: [
          "Watering trees",
          "Caring for flower beds",
          "Removing weeds and poisonous plants",
          "Trimming plants",
          "Cleaning public benches",
          "Maintaining walking trails",
          "Removing dog poop from walkways and trails",
          "Planting seasonal flowers",
          "Restoring community parks",
        ],
        outro: "Small improvements can transform entire neighborhoods.",
      },
      {
        heading: "Community beautification",
        body: ["Beautification projects create welcoming environments. Examples include:"],
        list: [
          "Public art",
          "Murals",
          "Flower planting",
          "Park improvements",
          "Community signs",
          "Outdoor seating",
          "Butterfly gardens",
          "Native landscaping",
          "Decorative planters",
        ],
        outro: "Beautiful neighborhoods encourage community involvement and local pride.",
      },
      {
        heading: "Wildlife protection",
        body: ["Healthy communities also protect local wildlife. Programs may include:"],
        list: [
          "Bird habitat restoration",
          "Pollinator gardens",
          "Nesting boxes",
          "Native tree planting",
          "Wildlife education",
          "Habitat conservation",
          "Responsible pet ownership education",
        ],
        outro: "Protecting nature benefits future generations.",
      },
      {
        heading: "Climate action",
        body: ["Everyone can help reduce environmental impacts. Simple actions include:"],
        list: [
          "Conserving energy",
          "Using renewable energy",
          "Planting trees",
          "Conserving water",
          "Walking or cycling when possible",
          "Supporting local agriculture",
          "Reducing waste",
          "Recycling",
          "Protecting green spaces",
        ],
        outro: "Small changes made by many people create meaningful results.",
      },
      {
        heading: "Community emergency preparedness",
        body: [
          "Prepared communities recover more quickly from disasters. Social Pulse encourages:",
        ],
        list: [
          "Emergency preparedness training",
          "Community response teams",
          "First aid education",
          "Fire prevention",
          "Flood preparedness",
          "Earthquake readiness",
          "Storm preparation",
          "Neighborhood communication plans",
        ],
        outro: "Preparedness helps save lives and protect property.",
      },
      {
        heading: "Neighborhood watch programs",
        body: [
          "Communities become safer when neighbors look out for one another. Neighborhood Watch initiatives encourage residents to:",
        ],
        list: [
          "Report suspicious activity to the appropriate authorities",
          "Help vulnerable neighbors during emergencies",
          "Promote crime prevention through awareness",
          "Improve neighborhood communication",
          "Support local law enforcement through lawful, non-confrontational observation",
          "Build stronger relationships among residents",
        ],
        outro: "A connected community is often a safer community.",
      },
      {
        heading: "Volunteer opportunities",
        body: ["Volunteers are the heart of community improvement. You can help by:"],
        list: [
          "Planting trees",
          "Maintaining community gardens",
          "Cleaning parks",
          "Picking up litter",
          "Delivering donated goods",
          "Organizing neighborhood events",
          "Teaching environmental education",
          "Helping seniors with yard work",
          "Mentoring youth",
          "Participating in disaster recovery projects",
        ],
        outro: "Every volunteer helps make a difference.",
      },
      {
        heading: "Environmental education",
        body: ["Knowledge leads to action. Educational programs may include:"],
        list: [
          "Recycling workshops",
          "Gardening classes",
          "Water conservation",
          "Wildlife protection",
          "Sustainable living",
          "Composting",
          "Energy conservation",
          "Climate awareness",
          "Community leadership",
          "Civic responsibility",
        ],
        outro:
          "Learning together creates stronger, more environmentally responsible communities.",
      },
      {
        heading: "How you can help",
        body: ["You can strengthen your community by:"],
        list: [
          "Volunteering at cleanup events",
          "Planting trees",
          "Starting or supporting a community garden",
          "Donating excess food or household goods",
          "Reporting public safety and sanitation concerns",
          "Helping maintain parks and public spaces",
          "Organizing neighborhood improvement projects",
          "Supporting recycling initiatives",
          "Teaching environmental stewardship",
          "Encouraging others to become involved",
        ],
        outro:
          "Together, we can build neighborhoods that are cleaner, greener, safer, and more connected. Every tree planted, every piece of litter removed, every garden nurtured, and every act of kindness helps create healthier communities and a healthier planet. By caring for our environment and for one another, we leave a legacy of hope, responsibility, and joy for generations to come.",
      },
      {
        heading: "Responsible citizenship",
        body: [
          "Beyond volunteering, Social Pulse encourages people to be active stewards of their communities by conserving water and energy, supporting local businesses, participating in neighborhood meetings, respecting public spaces, helping elderly or disabled neighbors, promoting kindness and inclusion, and working collaboratively with local governments and organizations to solve community challenges.",
          "Together, these everyday actions foster resilient, vibrant communities where both people and the environment can flourish.",
        ],
      },
    ],
    cta: {
      label: "Join a community cleanup",
      href: "/opportunities?category=environment",
      secondary_label: "Sponsor a green project",
      secondary_href: "/find-help?category=environment",
    },
    closing_heading: "Care for our environment. Care for each other.",
    closing_body:
      "Plant a tree, join a cleanup, share what you no longer need, or check on a neighbor. Every act of kindness leaves a legacy for the next generation.",
  },
};

export const findCategoryArticle = (slug) => CATEGORY_ARTICLES[slug];
